<script lang="ts">
  import ServerHeader from "@/components/ServerHeader.svelte";
  import ServerNavigation from "@/components/ServerNavigation.svelte";
  import { untrack } from "svelte";
  import { get } from "svelte/store";
  import { api, ApiError, type GameServer, type ServerDetails, type ServerResource } from "@/api";
  import { coreOptions, gameLabel, transitionalStatuses, SERVER_HOURLY_COST } from "@/config";
  import CoreVersionSelect from "@/components/CoreVersionSelect.svelte";
  import { routeHref, navigate } from "@/router";
  import { token as tokenStore } from "@/stores/session";
  import {
    dashboardRealtimeHealthy,
    dashboardRealtimeSubscribed,
    subscribeDashboardInvalidations,
  } from "@/stores/dashboard-realtime";
  import LiveConsole from "@/components/LiveConsole.svelte";
  import ServerAiChat from "@/components/ServerAiChat.svelte";
  import FileManager from "@/components/FileManager.svelte";
  import ServerTelemetry from "@/components/ServerTelemetry.svelte";
  import ServerStateNotice from "@/components/ServerStateNotice.svelte";
  import MinecraftRuntimeNotice from "@/components/MinecraftRuntimeNotice.svelte";
  import BackupPanel from "@/components/BackupPanel.svelte";
  import Button from "@/components/Button.svelte";
  import Select from "@/components/Select.svelte";
  import FormField from "@/components/FormField.svelte";
  import TextInput from "@/components/TextInput.svelte";
  import LoadingState from "@/components/LoadingState.svelte";
  import {
    isBillingSettlementPending,
    serverActionAvailability,
    type ServerAction,
  } from "@/server-state-policy";
  import { formatNumber } from "@/utils/format";

  import {
    connect as connectConsole,
    disconnect as disconnectConsole,
    consoleStatus,
  } from "@/stores/console";
  import { confirmModal } from "@/stores/modal";
  import {
    balance,
    feedback,
    servers,
    busyAction,
    serverActions,
    runServerAction,
    deleteServer,
  } from "@/stores/dashboard";

  export type ServerSection = "home" | "settings" | "console" | "files" | "ai" | "backups";

  const { id, section = "home" }: { id: string; section?: ServerSection } = $props();

  let details = $state<ServerDetails | null>(null);
  let error = $state("");
  let loading = $state(true);
  let notFound = $state(false);
  let actionError = $state("");
  let actionErrorCode = $state<string | undefined>();
  let settingsBusy = $state(false);
  let settingsError = $state("");
  let settingsNotice = $state("");
  let reinstallBusy = $state(false);
  let editName = $state("");
  let editCore = $state("");
  let editVersion = $state("");
  let editJavaVersion = $state(21);

  const server = $derived<GameServer | undefined>($servers.find((item) => item.id === id));
  const current = $derived(details ?? server);
  const actionStates = $derived({
    start: Boolean($serverActions[`start:${id}`]),
    restart: Boolean($serverActions[`restart:${id}`]),
    stop: Boolean($serverActions[`stop:${id}`]),
    kill: Boolean($serverActions[`kill:${id}`]),
  });
  const actionBusy = $derived(Object.values(actionStates).some(Boolean));
  const settingsWillWipe = $derived(
    current !== undefined && (editCore !== current.core || editVersion.trim() !== current.version),
  );
  const filesDisabled = $derived(current !== undefined && transitionalStatuses.has(current.status));

  const needsFrequentRefresh = $derived(
    filesDisabled ||
      (current !== undefined && isBillingSettlementPending(current)) ||
      actionErrorCode === "billing_settlement_pending",
  );

  function requireToken(): string {
    const value = get(tokenStore);
    if (!value) {
      throw new Error("нет активной сессии");
    }
    return value;
  }

  let refreshDetails: () => Promise<void> = () => Promise.resolve();
  let retimeDetails: () => void = () => {};

  function load(): Promise<void> {
    return refreshDetails().catch(() => {});
  }

  async function readDetails(
    requestedId: string,
    requestedToken: string,
    signal: AbortSignal,
  ): Promise<void> {
    const isCurrent = () =>
      id === requestedId && get(tokenStore) === requestedToken && !signal.aborted;
    try {
      const nextDetails = await api.server(requestedToken, requestedId, signal);
      if (!isCurrent()) {
        return;
      }
      // Фоновый опрос не должен перезаписывать несохранённые настройки.
      if (!details) {
        editName = nextDetails.name;
        editCore = nextDetails.core;
        editVersion = nextDetails.version;
        editJavaVersion = nextDetails.javaVersion;
      }
      details = nextDetails;
      if (
        actionErrorCode === "billing_settlement_pending" &&
        nextDetails.billingSettlementPending === false
      ) {
        actionError = "";
        actionErrorCode = undefined;
        const notice = get(feedback);
        if (notice?.code === "billing_settlement_pending" && notice.serverId === requestedId) {
          feedback.set(null);
        }
      }
      notFound = false;
      error = "";
    } catch (requestError) {
      if (!isCurrent() || (requestError as Error).name === "AbortError") {
        return;
      }
      notFound = requestError instanceof ApiError && requestError.status === 404;
      if (notFound && details && ["queued_delete", "deleting"].includes(details.status)) {
        navigate("servers");
        return;
      }
      error = (requestError as Error).message;
      if (!notFound) {
        throw requestError;
      }
    } finally {
      if (isCurrent()) {
        loading = false;
      }
    }
  }

  async function retryLoad() {
    if (loading) {
      return;
    }
    loading = true;
    await load();
  }

  // Each route/session owns one read and at most one coalesced follow-up.
  $effect(() => {
    const requestedId = id;
    const requestedToken = $tokenStore;
    details = null;
    error = "";
    notFound = false;
    loading = Boolean(requestedToken);
    actionError = "";
    actionErrorCode = undefined;
    settingsError = "";
    settingsNotice = "";
    const controller = new AbortController();
    let stopped = false;
    let dirty = false;
    let failures = 0;
    let flight: Promise<void> | null = null;
    let timer: number | undefined;
    let hidden = document.visibilityState === "hidden";
    const clearTimer = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
      timer = undefined;
    };
    const schedule = () => {
      clearTimer();
      const sessionEnded = stopped || !requestedToken;
      if (sessionEnded || hidden || flight) {
        return;
      }
      const base = needsFrequentRefresh ? 4_000 : get(dashboardRealtimeHealthy) ? 120_000 : 30_000;
      const delay = Math.min(120_000, base * 2 ** Math.min(failures, 5));
      timer = window.setTimeout(
        () => void request().catch(() => {}),
        Math.round(delay * (1 + Math.random() * 0.1)),
      );
    };
    const request = (): Promise<void> => {
      if (stopped || !requestedToken) {
        return Promise.resolve();
      }
      dirty = true;
      clearTimer();
      if (hidden) {
        return flight ?? Promise.resolve();
      }
      if (flight) {
        return flight;
      }
      flight = (async () => {
        while (dirty && !stopped && !hidden) {
          dirty = false;
          try {
            await readDetails(requestedId, requestedToken, controller.signal);
            failures = 0;
          } catch (requestError) {
            failures = Math.min(failures + 1, 5);
            throw requestError;
          }
        }
      })().finally(() => {
        flight = null;
        schedule();
      });
      return flight;
    };
    const visibilityChanged = () => {
      const nextHidden = document.visibilityState === "hidden";
      if (nextHidden === hidden) {
        return;
      }
      hidden = nextHidden;
      if (hidden) {
        clearTimer();
      } else if (needsFrequentRefresh || !get(dashboardRealtimeSubscribed)) {
        void request().catch(() => {});
      }
    };
    refreshDetails = request;
    retimeDetails = schedule;
    const unsubscribe = subscribeDashboardInvalidations((event) => {
      if (event.allServers || event.serverIds?.includes(requestedId)) {
        return request();
      }
    });
    document.addEventListener("visibilitychange", visibilityChanged);
    untrack(() => void request().catch(() => {}));
    return () => {
      stopped = true;
      clearTimer();
      controller.abort();
      unsubscribe();
      document.removeEventListener("visibilitychange", visibilityChanged);
      if (refreshDetails === request) {
        refreshDetails = () => Promise.resolve();
      }
      if (retimeDetails === schedule) {
        retimeDetails = () => {};
      }
    };
  });

  // Подключение консоли для соответствующих разделов.
  $effect(() => {
    void id;
    void section;
    if (section === "home" || section === "console") {
      connectConsole(id, requireToken());
    } else {
      disconnectConsole();
    }
    return () => disconnectConsole();
  });

  // Stable primitive inputs keep cloned REST rows from restarting the polling lifetime.
  $effect(() => {
    void needsFrequentRefresh;
    void $dashboardRealtimeHealthy;
    untrack(() => retimeDetails());
  });

  // Реагируем на новое live-событие, а не на каждое обновление REST-данных.
  $effect(() => {
    const liveStatus = $consoleStatus;
    untrack(() => {
      if (liveStatus && details && details.status !== liveStatus) {
        // A terminal process status does not prove that its last billing period is settled.
        details = {
          ...details,
          status: liveStatus,
          ...(liveStatus === "stopped" ? { billingSettlementPending: true } : {}),
        };
        void load();
      }
    });
  });

  function handleSettingsSubmit(event: SubmitEvent) {
    event.preventDefault();
    void saveSettings();
  }

  function handleBackupUpdated() {
    void load();
  }

  async function runAction(action: ServerAction) {
    if (!current || current.id !== id || !serverActionAvailability(current)[action]) {
      return;
    }
    if (action === "kill" ? actionStates.kill : actionBusy) {
      return;
    }
    const requestedId = id;
    const requestedToken = get(tokenStore);
    if (
      action === "kill" &&
      !(await confirmModal(
        "Принудительно остановить сервер?",
        "Сервер будет остановлен немедленно. Несохранённые данные могут быть потеряны.",
        "Убить сервер",
      ))
    ) {
      return;
    }
    if (
      id !== requestedId ||
      get(tokenStore) !== requestedToken ||
      !current ||
      current.id !== requestedId ||
      !serverActionAvailability(current)[action] ||
      (action === "kill" ? actionStates.kill : actionBusy)
    ) {
      return;
    }
    actionError = "";
    actionErrorCode = undefined;
    const accepted = await runServerAction(current, action);
    if (id !== requestedId || get(tokenStore) !== requestedToken) {
      return;
    }
    if (!accepted) {
      const notice = get(feedback);
      actionError = notice?.text ?? "Не удалось отправить действие.";
      actionErrorCode = notice?.code;
    }
    await load();
  }

  async function remove() {
    if (!server) {
      return;
    }
    if (
      !(await confirmModal(
        "Удалить сервер?",
        `Удалить «${server.name}»? Это действие нельзя отменить.`,
        "Удалить",
      ))
    ) {
      return;
    }
    if (await deleteServer(server)) {
      navigate("servers");
    }
  }

  async function reinstallServer() {
    if (!current || reinstallBusy) {
      return;
    }
    if (
      !(await confirmModal(
        "Переустановить сервер?",
        "Все файлы сервера будут удалены, включая мир и настройки, после чего выбранная сборка установится заново.",
        "Удалить и переустановить",
      ))
    ) {
      return;
    }
    reinstallBusy = true;
    settingsError = "";
    settingsNotice = "";
    try {
      details = {
        ...(await api.updateServerSettings(requireToken(), id, {
          reinstall: true,
          confirmWipe: true,
        })),
      } as ServerDetails;
      settingsNotice = "Сервер поставлен в очередь на переустановку.";
    } catch (requestError) {
      settingsError = (requestError as Error).message;
    } finally {
      reinstallBusy = false;
    }
  }

  async function saveSettings() {
    if (!current || settingsBusy) {
      return;
    }
    settingsError = "";
    settingsNotice = "";
    const coreChanged = editCore !== current.core;
    const versionChanged = editVersion.trim() !== current.version;
    const wipe = coreChanged || versionChanged;
    if (
      wipe &&
      !(await confirmModal(
        "Удалить файлы сервера?",
        "Смена сборки или версии Minecraft удалит все файлы, включая мир. Перед подтверждением сохраните резервную копию или скачайте нужные файлы.",
        "Удалить и применить",
      ))
    ) {
      return;
    }
    settingsBusy = true;
    try {
      const updated = await api.updateServerSettings(requireToken(), id, {
        name: editName.trim(),
        core: editCore,
        version: editVersion.trim(),
        javaVersion: editJavaVersion,
        ...(wipe ? { confirmWipe: true } : {}),
      });
      details = { ...details, ...updated } as ServerDetails;
      settingsNotice = wipe
        ? "Сервер поставлен в очередь на остановку и переустановку."
        : "Настройки сохранены.";
    } catch (requestError) {
      settingsError = (requestError as Error).message;
    } finally {
      settingsBusy = false;
    }
  }

  let upgradeBusy = $state<ServerResource | null>(null);
  let upgradeError = $state("");
  let upgradeNotice = $state("");

  function formatGb(mb: number): string {
    if (mb % 1024 === 0) {
      return `${mb / 1024} ГБ`;
    }
    const gb = mb / 1024;
    return `${Number(gb.toFixed(2))} ГБ`;
  }

  const resourcePlans = $derived([
    {
      resource: "ram" as const,
      label: "ОЗУ",
      valueLabel: formatGb(current?.memLimitMb ?? 1024),
      step: "+256 МБ",
      cost: 200,
      maxed: (current?.memLimitMb ?? 1024) >= 8192,
    },
    {
      resource: "disk" as const,
      label: "Диск",
      valueLabel: formatGb(current?.diskLimitMb ?? 1024),
      step: "+512 МБ",
      cost: 120,
      maxed: (current?.diskLimitMb ?? 1024) >= 32768,
    },
    {
      resource: "players" as const,
      label: "Игроки",
      valueLabel: `${current?.playerLimit ?? 5}`,
      step: "+1",
      cost: 30,
      maxed: (current?.playerLimit ?? 5) >= 100,
    },
    {
      resource: "cpu" as const,
      label: "CPU",
      valueLabel: `${current?.cpuLimitPercent ?? 50}%`,
      step: "+25%",
      cost: 400,
      maxed: (current?.cpuLimitPercent ?? 50) >= 200,
    },
  ]);

  async function upgrade(
    resource: ServerResource,
    label: string,
    step: string,
    cost: number,
  ): Promise<void> {
    if ($balance < cost) {
      const message = `Недостаточно кредитов: нужно ${cost}, баланс ${$balance}`;
      upgradeError = message;
      feedback.set({ kind: "error", text: message });
      return;
    }
    if (
      upgradeBusy ||
      !(await confirmModal(`Улучшить ${label}?`, `${step} за ${cost} кредитов.`, "Улучшить"))
    ) {
      return;
    }
    upgradeBusy = resource;
    upgradeError = "";
    upgradeNotice = "";
    try {
      const result = await api.upgradeResource(requireToken(), id, resource);
      const text = `${result.label}: улучшено (${result.stepLabel}) за ${result.cost} кредитов`;
      upgradeNotice = text;
      feedback.set({ kind: "success", text });
      balance.update((prev) => Math.max(0, prev - result.cost));
      await load().catch(() => {});
    } catch (requestError) {
      const message = (requestError as Error).message;
      upgradeError = message;
      feedback.set({ kind: "error", text: message });
    } finally {
      upgradeBusy = null;
    }
  }
</script>

{#if (loading && !details) || !current || notFound}
  {#if loading && !notFound}
    <LoadingState message="Загружаем сервер…" />
  {:else}
    <section class="panel-card route-empty">
      <p class="eyebrow">Серверы</p>
      <h1>{notFound ? "Сервер не найден" : "Не удалось загрузить сервер"}</h1>
      <p>
        {notFound
          ? "Возможно, он был удалён или ссылка устарела."
          : error || "Попробуйте загрузить данные ещё раз."}
      </p>
      {#if !notFound}<Button disabled={loading} onclick={retryLoad}
          >{loading ? "Загружаем…" : "Повторить загрузку"}</Button
        >{/if}
      <a class="back-link" href={routeHref("servers")}>Вернуться к серверам</a>
    </section>
  {/if}
{:else}
  <ServerHeader server={current} busyActions={actionStates} onAction={runAction} />

  {#if error}
    <div class="form-error" role="alert">
      Не удалось обновить состояние. {error}
      <Button variant="ghost" disabled={loading} onclick={retryLoad}
        >{loading ? "Обновляем…" : "Обновить данные"}</Button
      >
    </div>
  {/if}
  {#if actionError}<div class="form-error" role="alert">{actionError}</div>{/if}
  <MinecraftRuntimeNotice
    core={current.core}
    version={current.version}
    memoryMb={current.memLimitMb}
  />
  <ServerStateNotice
    server={current}
    retryBusy={actionBusy}
    onRetry={() => void runAction("start")}
  />

  <ServerNavigation {id} core={current.core} {section} />

  {#if section === "home"}
    <div class="detail-grid">
      <ServerTelemetry serverId={id} status={current.status} part="players" />
      <ServerTelemetry serverId={id} status={current.status} part="metrics" />
      <section class="panel-card" aria-labelledby="connection-title">
        <header class="panel-heading">
          <div>
            <p class="eyebrow">Рабочее состояние</p>
            <h2 id="connection-title">Параметры сервера</h2>
          </div>
        </header>
        <div class="detail-card-body">
          <dl class="detail-specs">
            <div>
              <dt>Игра</dt>
              <dd>{gameLabel(current.game)}</dd>
            </div>
            <div>
              <dt>Сборка</dt>
              <dd>{current.core}</dd>
            </div>
            <div>
              <dt>Память</dt>
              <dd>{formatNumber(current.memLimitMb)} МБ</dd>
            </div>
            <div>
              <dt>Тариф</dt>
              <dd>{current.hourlyCost ?? SERVER_HOURLY_COST} кр. / час</dd>
            </div>
            {#if details?.queuePosition != null}
              <div>
                <dt>
                  {current.status === "queued_start" ? "Очередь запуска" : "Очередь установки"}
                </dt>
                <dd>
                  Позиция {details.queuePosition}{#if current.status === "queued_start"}
                    · ожидает запуска{/if}
                </dd>
              </div>
            {/if}
          </dl>
        </div>
      </section>

      <LiveConsole />
    </div>
  {:else if section === "backups"}
    <BackupPanel
      serverId={id}
      serverStatus={current.status}
      enabled={current.backupsEnabled}
      slots={current.backupSlots ?? (current.backupsEnabled ? 1 : 0)}
      onUpdated={handleBackupUpdated}
    />
  {:else if section === "ai"}
    {#key id}<ServerAiChat serverId={id} />{/key}
  {:else if section === "console"}
    <LiveConsole />
  {:else if section === "files"}
    <FileManager serverId={id} disabled={filesDisabled} serverStatus={current?.status} />
  {:else}
    <section class="settings-grid">
      <section class="panel-card settings-card" aria-labelledby="settings-title">
        <header class="panel-heading">
          <div>
            <p class="eyebrow">Параметры сервера</p>
            <h2 id="settings-title">Настройки</h2>
          </div>
        </header>

        {#if settingsError}
          <div class="upgrade-error" role="alert">{settingsError}</div>
        {/if}
        {#if settingsNotice}
          <div class="upgrade-notice">{settingsNotice}</div>
        {/if}

        <form class="server-settings-form" onsubmit={handleSettingsSubmit}>
          <FormField label="Название"
            ><TextInput bind:value={editName} maxlength={48} required /></FormField
          >
          <FormField label="Игра"><TextInput value={gameLabel(current.game)} disabled /></FormField>
          <div>
            <span class="settings-field-label">Сборка сервера (ядро)</span>
            <Select
              aria-label="Сборка сервера (ядро)"
              bind:value={editCore}
              options={coreOptions}
            />
          </div>
          <div>
            <span class="settings-field-label">Версия игры</span>
            <CoreVersionSelect core={editCore} bind:value={editVersion} preserveUnavailable />
            <MinecraftRuntimeNotice
              core={editCore}
              version={editVersion}
              memoryMb={current.memLimitMb}
            />
          </div>
          <div>
            <span class="settings-field-label">Java для запуска сервера</span>
            <Select
              aria-label="Java для запуска сервера"
              bind:value={editJavaVersion}
              disabled={current.status !== "stopped" || false}
              options={[8, 11, 17, 21, 25].map((j) => ({ value: j, label: `Java ${j}` }))}
            />
          </div>
          <p class="settings-help">
            Сборка определяет поддержку модов и плагинов. Java (JRE) — среда для её запуска;
            выбирайте версию по требованиям сборки.
          </p>
          {#if current.status !== "stopped"}
            <p class="settings-hint">
              Для смены Java сначала остановите сервер. Смена Java сохраняет файлы.
            </p>
          {/if}
          <div class="settings-readonly">
            <span>Память</span>
            <strong>{formatNumber(current.memLimitMb)} МБ</strong>
          </div>
          <div class="settings-readonly">
            <span>Код в адресе подключения</span>
            <strong>{current.joinCode}</strong>
          </div>
          {#if settingsWillWipe}
            <div class="settings-warning" role="status">
              <strong>Применение удалит мир и остальные файлы</strong>
              <p>
                Вы изменили сборку или версию Minecraft. Перед сохранением создайте резервную копию
                в разделе «Копии».
              </p>
            </div>
          {/if}
          <Button
            class="settings-submit"
            type="submit"
            disabled={settingsBusy || reinstallBusy || transitionalStatuses.has(current.status)}
          >
            {settingsBusy
              ? "Сохраняем…"
              : settingsWillWipe
                ? "Удалить файлы и применить"
                : "Сохранить настройки"}
          </Button>
          <p class="settings-help">
            Переустановка удаляет все файлы, включая мир. <a
              href={routeHref("server-backups", { id })}>Создайте резервную копию</a
            > перед переустановкой.
          </p>
          <Button
            variant="ghost"
            class="settings-reinstall"
            onclick={reinstallServer}
            disabled={settingsBusy || reinstallBusy || transitionalStatuses.has(current.status)}
          >
            {reinstallBusy ? "Переустанавливаем…" : "Переустановить сервер"}
          </Button>
        </form>
      </section>

      <section class="panel-card settings-card" aria-labelledby="resources-title">
        <header class="panel-heading">
          <div>
            <p class="eyebrow">Ресурсы</p>
            <h2 id="resources-title">Лимиты и улучшения</h2>
          </div>
        </header>

        {#if upgradeError}
          <div class="upgrade-error" role="alert">{upgradeError}</div>
        {/if}
        {#if upgradeNotice}
          <div class="upgrade-notice">{upgradeNotice}</div>
        {/if}

        <div class="upgrade-list">
          {#each resourcePlans as plan (plan.resource)}
            <div class="upgrade-row">
              <div>
                <strong>{plan.label}</strong>
                <span>сейчас: {plan.valueLabel}</span>
              </div>
              <button
                type="button"
                disabled={upgradeBusy !== null || plan.maxed}
                onclick={() => void upgrade(plan.resource, plan.label, plan.step, plan.cost)}
              >
                {#if upgradeBusy === plan.resource}
                  Покупаем…
                {:else if plan.maxed}
                  Максимум
                {:else}
                  {plan.step} · {plan.cost} кр
                {/if}
              </button>
            </div>
          {/each}
        </div>
        <p class="settings-note">
          Улучшения постоянны и применяются при следующем запуске сервера.
        </p>
      </section>
    </section>
  {/if}

  {#if section === "settings"}
    <section class="danger-zone" aria-labelledby="danger-title">
      <div>
        <p class="eyebrow">Необратимое действие</p>
        <strong id="danger-title">Удалить сервер</strong>
        <span>Контейнер и файлы будут удалены безвозвратно.</span>
      </div>
      <button type="button" disabled={$busyAction === `delete:${id}`} onclick={remove}>
        {$busyAction === `delete:${id}` ? "Удаляем…" : "Удалить"}
      </button>
    </section>
  {/if}
{/if}

<style>
  .settings-help {
    grid-column: 1 / -1;
    margin: 0;
    color: var(--muted);
    font-size: 0.82rem;
    line-height: 1.6;
  }
  .settings-warning {
    grid-column: 1 / -1;
    padding: 1rem;
    border: 1px solid var(--amber);
    background: rgb(232 162 77 / 0.07);
    font-size: 0.82rem;
    line-height: 1.6;
  }
  .settings-warning p {
    margin: 0.4rem 0 0;
    color: var(--muted);
  }

  .detail-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1.5rem;
  }

  .settings-grid {
    display: grid;
    gap: 1.5rem;
  }

  .server-settings-form {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    padding: 1.5rem;
  }
  .settings-readonly {
    padding: 0.55rem 0;
  }
  .settings-readonly strong {
    color: var(--cream);
    font-size: 0.82rem;
    overflow-wrap: anywhere;
  }
  .server-settings-form :global(.settings-submit) {
    grid-column: 1 / -1;
  }
  .server-settings-form :global(.settings-reinstall) {
    grid-column: 1 / -1;
    justify-self: start;
    padding: 0.65rem 0.85rem;
    border: 1px solid rgb(232 162 77 / 0.55);
    color: var(--amber);
    background: transparent;
    font-size: 0.7rem;
  }
  .server-settings-form :global(.settings-reinstall:hover:not(:disabled)) {
    color: var(--ink);
    background: var(--amber);
  }
  .settings-note {
    margin: 0;
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--line);
    color: var(--muted);
    font-size: 0.75rem;
    line-height: 1.55;
  }

  .upgrade-list {
    display: grid;
  }
  .upgrade-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--line);
  }
  .upgrade-row:last-of-type {
    border-bottom: 0;
  }
  .upgrade-row div {
    display: grid;
    gap: 0.3rem;
  }
  .upgrade-row strong {
    font: 500 0.78rem/1 var(--font-display);
    text-transform: uppercase;
  }
  .upgrade-row span {
    color: var(--muted);
    font-size: 0.72rem;
  }
  .upgrade-row button {
    flex: 0 0 auto;
    padding: 0.6rem 0.85rem;
    border: 1px solid var(--line-strong);
    color: var(--mars-light);
    background: transparent;
    font-size: 0.7rem;
  }
  .upgrade-row button:hover:not(:disabled) {
    background: rgb(214 75 50 / 0.1);
  }
  .upgrade-error,
  .upgrade-notice {
    padding: 0.7rem 1.5rem;
    border-bottom: 1px solid var(--line);
    font-size: 0.72rem;
  }
  .upgrade-error {
    color: var(--mars-light);
    background: rgb(214 75 50 / 0.08);
  }
  .upgrade-notice {
    color: var(--green);
    background: rgb(154 189 143 / 0.06);
  }

  .settings-field-label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
    color: var(--dim);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .detail-card-body {
    padding: 1.5rem;
  }

  .detail-specs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: 1px;
    margin: 0;
    border: 1px solid var(--line);
    background: var(--line);
  }

  .detail-specs > div {
    padding: 0.9rem 1rem;
    background: var(--surface);
  }

  .detail-specs dt {
    color: var(--dim);
    font-size: 0.62rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .detail-specs dd {
    margin: 0.35rem 0 0;
    color: var(--cream);
    font-size: 0.82rem;
  }

  .danger-zone {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 2rem;
    padding: 1.4rem 1.5rem;
    border: 1px solid rgb(214 75 50 / 0.3);
    background: rgb(214 75 50 / 0.05);
  }

  .danger-zone div {
    display: grid;
    gap: 0.3rem;
  }

  .danger-zone strong {
    font-size: 0.9rem;
  }

  .danger-zone span {
    color: var(--dim);
    font-size: 0.72rem;
  }

  .danger-zone button {
    flex: 0 0 auto;
    padding: 0.65rem 0.9rem;
    border: 1px solid rgb(214 75 50 / 0.4);
    color: var(--mars-light);
    background: transparent;
    font-size: 0.74rem;
  }

  .danger-zone button:hover:not(:disabled) {
    color: var(--white);
    background: var(--action);
  }

  @media (min-width: 900px) {
    .detail-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: start;
    }
  }

  @media (max-width: 640px) {
  }

  @media (max-width: 520px) {
    .server-settings-form {
      grid-template-columns: 1fr;
      padding: 1rem;
    }
    .server-settings-form :global(.settings-submit) {
      grid-column: auto;
      width: 100%;
    }
    .server-settings-form :global(.settings-reinstall) {
      grid-column: auto;
      width: 100%;
    }
    .danger-zone {
      align-items: flex-start;
      flex-direction: column;
    }
  }
</style>
